import React, { Fragment, useState } from 'react';
import './rush.css';
import './pricing.css';
import { RushNav, RushFooter, useRushReveal } from './RushChrome';
import { Link } from 'react-router-dom';

const PRICING_PLANS = [
  { name: 'Free', monthly: 0, description: 'For solo translators and first projects.', features: ['1 Project', '5,000 Words / mo', 'Translation memory', 'Community support'], cta: 'Start free', highlight: false },
  { name: 'Essential', monthly: 80, description: 'Perfect for small teams and startups.', features: ['20 Projects', '50,000 Words / mo', '1 App Integration', 'Email Support'], cta: 'Get Started', highlight: false },
  { name: 'Growth', monthly: 250, description: 'For growing companies with global reach.', features: ['200 Projects', '500,000 Words / mo', '5 App Integrations', 'Priority Support'], cta: 'Upgrade Now', highlight: true },
  { name: 'Scale', monthly: 500, description: 'Advanced features for large scale operations.', features: ['Unlimited Projects', 'Unlimited Words', 'Unlimited Apps', 'Dedicated Support'], cta: 'Contact Sales', highlight: false },
];

const COMPARE_GROUPS = [
  {
    label: 'Usage',
    rows: [
      ['Projects', '1', '20', '200', 'Unlimited'],
      ['Words / month', '5,000', '50,000', '500,000', 'Unlimited'],
      ['Languages', '100+', '100+', '100+', '100+'],
      ['Team seats', '1', '3 included', '10 included', 'Unlimited'],
    ],
  },
  {
    label: 'Translation features',
    rows: [
      ['AI machine translation', true, true, true, true],
      ['Translation memory (TM)', true, true, true, true],
      ['Glossaries', false, '1 per workspace', 'Per client', 'Per client + on-prem'],
      ['Side-by-side editor', true, true, true, true],
      ['Real-time review & comments', false, false, true, true],
      ['Custom MT engines', false, false, false, true],
    ],
  },
  {
    label: 'Integrations',
    rows: [
      ['App integrations', false, '1', '5', 'Unlimited'],
      ['Google Drive · Dropbox · OneDrive', false, true, true, true],
      ['Slack · Notion · GitHub', false, false, true, true],
      ['API + webhooks', false, false, true, true],
    ],
  },
  {
    label: 'Security & support',
    rows: [
      ['SOC 2 + GDPR', true, true, true, true],
      ['SSO & SAML', false, false, 'Add-on', true],
      ['SCIM provisioning', false, false, false, true],
      ['Audit logs', false, false, false, true],
      ['Support', 'Community', 'Email', 'Priority email + chat', 'Dedicated CSM + SLA'],
    ],
  },
];

const Check = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path d="M3.5 9.5l3.5 3.5 7-7" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const Dash = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M3 7h8" stroke="var(--ink-4)" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CompareCell = ({ v }) => {
  if (v === true) return <span className="cmp-true"><Check /></span>;
  if (v === false) return <span className="cmp-false"><Dash /></span>;
  return <span className="cmp-text">{v}</span>;
};

const PricingHero = ({ billing, setBilling }) => {
  const yearly = billing === 'yearly';
  return (
    <section className="pricing-hero">
      <div className="hero-mesh"></div>
      <div className="rs-container hero-anim" style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
        <span className="hero-eyebrow"><span className="dot"></span>Simple, transparent pricing</span>
        <h1 className="pricing-h1">Pricing that grows with your team.</h1>
        <p className="pricing-lead">Start free, scale globally. Every plan includes 100+ languages, translation memory, and the full dashboard. No setup fees, cancel any time.</p>
        <div className="billing-toggle" role="tablist" aria-label="Billing period">
          <button role="tab" aria-selected={!yearly} className={`billing-btn ${!yearly ? 'is-active' : ''}`} onClick={() => setBilling('monthly')}>Monthly</button>
          <button role="tab" aria-selected={yearly} className={`billing-btn ${yearly ? 'is-active' : ''}`} onClick={() => setBilling('yearly')}>Yearly <span className="billing-save">2 months free</span></button>
        </div>
      </div>
    </section>
  );
};

const PlanCards = ({ billing }) => {
  const yearly = billing === 'yearly';
  return (
    <section className="section-tight" style={{ paddingTop: 0 }}>
      <div className="rs-container">
        <div className="pricing stagger reveal">
          {PRICING_PLANS.map((plan) => {
            const isFree = plan.monthly === 0;
            const price = isFree ? 0 : (yearly ? plan.monthly * 10 : plan.monthly);
            const period = isFree ? 'forever' : (yearly ? '/ year' : '/ month');
            const monthlyEq = (!isFree && yearly) ? Math.round((plan.monthly * 10) / 12) : null;
            return (
              <div key={plan.name} className={`price-card ${plan.highlight ? 'featured' : ''}`}>
                {plan.highlight && <span className="price-tag">Most Popular</span>}
                <div className="price-name">{plan.name}</div>
                <div className="price-amount">
                  <span className="price-currency">£</span>
                  <span className="num">{price.toLocaleString()}</span>
                  <span className="per">{period}</span>
                </div>
                {monthlyEq && <div className="price-sub" style={{ fontSize: 12, marginTop: -8 }}>That's £{monthlyEq}/month, billed yearly</div>}
                <div className="price-sub">{plan.description}</div>
                <ul className="price-features">
                  {plan.features.map((f) => (
                    <li key={f} className="price-feature">
                      <svg className="price-check" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button className={`btn ${plan.highlight ? 'btn-accent' : 'btn-ghost'}`} style={{ marginTop: 'auto' }}>{plan.cta}</button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const FoundingNote = () => (
  <section className="section-tight" style={{ paddingTop: 8 }}>
    <div className="rs-container">
      <div className="reveal" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 18, textAlign: 'center', background: 'var(--accent-soft)', border: '1px solid var(--line)', borderRadius: 16, padding: '22px 28px', maxWidth: 880, margin: '0 auto' }}>
        <span style={{ fontSize: 15, lineHeight: 1.5, color: 'var(--ink-2)', maxWidth: 600 }}>
          <strong style={{ color: 'var(--ink)' }}>Pre-launch:</strong> founding customers who join early access lock in these prices for life — and help shape what we build before we launch.
        </span>
        <Link className="btn btn-accent btn-sm" to="/customers">Join early access →</Link>
      </div>
    </div>
  </section>
);

const TrustStrip = () => (
  <section className="section-tight">
    <div className="rs-container">
      <div className="trust-grid reveal stagger">
        <div className="trust-card">
          <div className="trust-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="M9 12l2.5 2.5L16 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </div>
          <h4>14-day money back</h4>
          <p>Try Rush Studio risk-free. If it's not a perfect fit, get a full refund within 14 days.</p>
        </div>
        <div className="trust-card">
          <div className="trust-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="4" y="10" width="16" height="11" rx="2" stroke="currentColor" strokeWidth="1.8" /><path d="M8 10V7a4 4 0 018 0v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
          </div>
          <h4>SSL encrypted</h4>
          <p>Every payment, every file, every line of memory is protected by 256-bit SSL encryption.</p>
        </div>
        <div className="trust-card">
          <div className="trust-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="2" y="6" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" /><path d="M2 11h20" stroke="currentColor" strokeWidth="1.8" /></svg>
          </div>
          <h4>Pay any way</h4>
          <div className="pay-methods">
            <span className="pay-chip">VISA</span>
            <span className="pay-chip">Mastercard</span>
            <span className="pay-chip">Amex</span>
            <span className="pay-chip">PayPal</span>
            <span className="pay-chip">SEPA</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Compare = () => (
  <section className="section" id="compare">
    <div className="rs-container">
      <div className="section-head reveal">
        <div className="eyebrow">Compare plans</div>
        <h2>The fine print, in plain English.</h2>
        <p className="section-lead">Every plan includes the full Rush Studio dashboard. These are the differences.</p>
      </div>

      <div className="cmp-table reveal">
        <div className="cmp-row cmp-head">
          <div className="cmp-cell cmp-label-cell"></div>
          <div className="cmp-cell"><div className="cmp-plan-name">Free</div><div className="cmp-plan-price">£0<span>/mo</span></div></div>
          <div className="cmp-cell"><div className="cmp-plan-name">Essential</div><div className="cmp-plan-price">£80<span>/mo</span></div></div>
          <div className="cmp-cell cmp-cell-featured"><div className="cmp-plan-name">Growth <span className="cmp-tag">Popular</span></div><div className="cmp-plan-price">£250<span>/mo</span></div></div>
          <div className="cmp-cell"><div className="cmp-plan-name">Scale</div><div className="cmp-plan-price">£500<span>/mo</span></div></div>
        </div>

        {COMPARE_GROUPS.map((group) => (
          <Fragment key={group.label}>
            <div className="cmp-row cmp-group-head">
              <div className="cmp-cell cmp-label-cell">{group.label}</div>
              <div className="cmp-cell"></div>
              <div className="cmp-cell"></div>
              <div className="cmp-cell cmp-cell-featured"></div>
              <div className="cmp-cell"></div>
            </div>
            {group.rows.map((row) => (
              <div className="cmp-row" key={row[0]}>
                <div className="cmp-cell cmp-label-cell">{row[0]}</div>
                <div className="cmp-cell"><CompareCell v={row[1]} /></div>
                <div className="cmp-cell"><CompareCell v={row[2]} /></div>
                <div className="cmp-cell cmp-cell-featured"><CompareCell v={row[3]} /></div>
                <div className="cmp-cell"><CompareCell v={row[4]} /></div>
              </div>
            ))}
          </Fragment>
        ))}

        <div className="cmp-row cmp-foot">
          <div className="cmp-cell cmp-label-cell"></div>
          <div className="cmp-cell"><button className="btn btn-ghost btn-sm">Start free</button></div>
          <div className="cmp-cell"><button className="btn btn-ghost btn-sm">Get Started</button></div>
          <div className="cmp-cell cmp-cell-featured"><button className="btn btn-accent btn-sm">Upgrade Now</button></div>
          <div className="cmp-cell"><button className="btn btn-ghost btn-sm">Contact Sales</button></div>
        </div>
      </div>
    </div>
  </section>
);

const FAQS = [
  { q: 'Is there really a free plan?', a: "Yes. The Free plan covers 1 project and 5,000 words a month with translation memory included — no card required. It's a real plan, not a time-limited trial, so solo translators and first projects can stay on it as long as they like." },
  { q: 'Can I switch plans later?', a: "Yes — upgrade, downgrade, or cancel any time. Changes take effect at the start of your next billing cycle, and we'll pro-rate the difference." },
  { q: 'What counts as a "word"?', a: "We count source words (the original text you upload), not target words. Translation memory matches at 100% are free — you only pay for new words. So re-translating content you've already done never counts against your allowance." },
  { q: 'Do unused words roll over?', a: 'Up to 50% of your monthly allowance rolls over to the next month on Growth and Scale plans. Free and Essential reset each month.' },
  { q: 'What if I need more words or projects?', a: 'Easy — add word packs from your dashboard at any time, or upgrade to the next plan. Scale customers have unlimited usage included.' },
  { q: 'Do you offer discounts for non-profits or academic use?', a: 'Yes. We offer 30% off any plan for registered non-profits, accredited universities, and open-source projects. Email hello@rushstudio.com with proof.' },
  { q: 'How does the 14-day money-back guarantee work?', a: "If Rush Studio isn't right for you, email us within 14 days of your first payment and we'll refund you in full — no questions asked." },
  { q: 'Is my data secure?', a: 'Rush Studio is SOC 2 Type II certified and GDPR-compliant. All data is encrypted in transit (TLS 1.3) and at rest (AES-256). Scale customers can self-host translation memory on-premises.' },
  { q: 'Can I get a custom plan?', a: 'Of course. If you need more than Scale offers — on-prem deployment, custom MT engines, dedicated infrastructure, or volume pricing — talk to our sales team.' },
];

const FAQItem = ({ q, a, open, onToggle }) => (
  <div className={`faq-item ${open ? 'open' : ''}`}>
    <button className="faq-q" onClick={onToggle} aria-expanded={open}>
      <span>{q}</span>
      <span className="faq-icon" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M4 7l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </span>
    </button>
    <div className="faq-a-wrap">
      <p className="faq-a">{a}</p>
    </div>
  </div>
);

const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="section" id="faq">
      <div className="rs-container faq-container">
        <div className="section-head section-head-left reveal" style={{ textAlign: 'left', margin: '0 0 40px' }}>
          <div className="eyebrow">FAQ</div>
          <h2 style={{ fontSize: 36 }}>Questions, answered.</h2>
        </div>
        <div className="faq-list reveal">
          {FAQS.map((item, i) => (
            <FAQItem key={i} q={item.q} a={item.a} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
          ))}
        </div>
        <div className="faq-aside reveal">
          <div className="faq-aside-card">
            <h4>Still have questions?</h4>
            <p>Our team replies in under 2 business hours, every business day.</p>
            <button className="btn btn-primary btn-sm">Talk to sales</button>
            <button className="btn btn-ghost btn-sm">Read the docs</button>
          </div>
        </div>
      </div>
    </section>
  );
};

const FinalCTA = () => (
  <section className="section">
    <div className="rs-container">
      <div className="final-cta reveal reveal-scale">
        <h2>Ready to ship languages, not files?</h2>
        <p>Start free, no card required — or join early access to lock in founding-customer pricing.</p>
        <form onSubmit={(e) => e.preventDefault()} style={{ display: 'inline-flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          <input className="input input-lg" placeholder="work@yourcompany.com" style={{ width: 300 }} />
          <button className="btn btn-accent btn-lg" type="submit">Start free →</button>
        </form>
      </div>
    </div>
  </section>
);

const RushPricingPage = () => {
  const [billing, setBilling] = useState('monthly');
  useRushReveal();
  return (
    <div className="rs">
      <RushNav active="pricing" />
      <PricingHero billing={billing} setBilling={setBilling} />
      <PlanCards billing={billing} />
      <FoundingNote />
      <TrustStrip />
      <Compare />
      <FAQ />
      <FinalCTA />
      <RushFooter />
    </div>
  );
};

export default RushPricingPage;
